import { apiClient } from '../utils/api-client';

/**
 * The stylized drawing of a crag sector, made from its photo on the server. See
 * docs/CRAG_SECTOR_DRAWING.md.
 */

export type SectorDrawingStatus = 'queued' | 'running' | 'ready' | 'failed';

export interface SectorDrawing {
  id: string;
  status: SectorDrawingStatus;
  error_code: string | null;
  image_url: string | null;
  /** Whether this one is standing in for the sector's photo right now. */
  applied: boolean;
  created_at: string;
}

export interface SectorDrawings {
  drawings: SectorDrawing[];
  /** The real photo, even while a drawing has taken its place. */
  sourceUrl: string | null;
  canRestore: boolean;
}

/** A refusal the panel has its own words for: `code` names them. */
export class SectorDrawingError extends Error {
  readonly code: string;

  constructor(code: string) {
    super(code);
    this.name = 'SectorDrawingError';
    this.code = code;
  }
}

async function read(response: Response): Promise<SectorDrawings> {
  if (!response.ok) {
    const body = await response.json().catch(() => null);
    throw new SectorDrawingError(body?.errors?.[0]?.code ?? 'unknown');
  }

  const body = await response.json();
  return { drawings: body.data, sourceUrl: body.meta.source_url, canRestore: body.meta.can_restore };
}

class SectorDrawingsService {
  private path(crag: string, sectorId: string) {
    return `/api/crags/${crag}/zones/${sectorId}/drawings`;
  }

  async list(crag: string, sectorId: string) {
    return read(await apiClient.get(this.path(crag, sectorId)));
  }

  /** Each press is a paid call: only ever from a button somebody pressed. */
  async generate(crag: string, sectorId: string) {
    return read(await apiClient.post(this.path(crag, sectorId), {}));
  }

  async apply(crag: string, sectorId: string, drawingId: string) {
    return read(await apiClient.post(`${this.path(crag, sectorId)}/${drawingId}/apply`, {}));
  }

  async restore(crag: string, sectorId: string) {
    return read(await apiClient.post(`${this.path(crag, sectorId)}/restore`, {}));
  }
}

export const sectorDrawingsService = new SectorDrawingsService();
